// frontend/src/pages/admin/Transactions.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../config/axios';
import {
    CreditCard, RefreshCw, Search, ChevronLeft, ChevronRight,
    TrendingUp, TrendingDown, Clock, XCircle
} from 'lucide-react';

// ─── Constantes ───────────────────────────────────────────────────────────────
const TX_TYPES = {
    DEPOSIT:                       'Depósito',
    WITHDRAWAL:                    'Retiro',
    TOURNAMENT_ENTRY:              'Entrada a Torneo',
    TOURNAMENT_RESERVATION:        'Reserva',
    TOURNAMENT_RESERVATION_RELEASE:'Dev. Reserva',
    TOURNAMENT_PRIZE:              'Premio',
    ADMIN_ADJUSTMENT:              'Ajuste Admin',
    REFUND:                        'Reembolso',
};

const TX_STATUS = {
    PENDING:   { label: 'Pendiente',  cls: 'bg-yellow-900 text-yellow-300' },
    COMPLETED: { label: 'Completada', cls: 'bg-green-900  text-green-300'  },
    REJECTED:  { label: 'Rechazada',  cls: 'bg-red-900    text-red-300'    },
    CANCELLED: { label: 'Cancelada',  cls: 'bg-gray-700   text-gray-300'   },
};

const TYPE_COLORS = {
    DEPOSIT:                       '#22c55e',
    WITHDRAWAL:                    '#ef4444',
    TOURNAMENT_ENTRY:              '#f59e0b',
    TOURNAMENT_RESERVATION:        '#a855f7',
    TOURNAMENT_RESERVATION_RELEASE:'#60a5fa',
    TOURNAMENT_PRIZE:              '#FCE503',
    ADMIN_ADJUSTMENT:              '#9ca3af',
    REFUND:                        '#f97316',
};

const PAGE_SIZE = 25;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (iso) =>
    new Date(iso).toLocaleString('es-AR', {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit',
    });

// ─── Componentes ─────────────────────────────────────────────────────────────
const SummaryCard = ({ icon: Icon, iconColor, label, value, sub }) => (
    <div className="rounded-xl p-5 flex flex-col gap-3" style={{ backgroundColor: '#0E2238' }}>
        <div className="flex items-center justify-between">
            <span className="text-sm font-semibold uppercase tracking-wide text-gray-400">{label}</span>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ backgroundColor: `${iconColor}22` }}>
                <Icon className="w-5 h-5" style={{ color: iconColor }} />
            </div>
        </div>
        <p className="text-4xl font-bold text-white">{value ?? '—'}</p>
        {sub && <p className="text-xs text-gray-500">{sub}</p>}
    </div>
);

const TypeBadge = ({ type }) => (
    <span
        className="px-2 py-0.5 rounded-full text-xs font-semibold"
        style={{ backgroundColor: `${TYPE_COLORS[type] || '#9ca3af'}22`, color: TYPE_COLORS[type] || '#9ca3af' }}
    >
        {TX_TYPES[type] || type}
    </span>
);

const StatusBadge = ({ status }) => {
    const { label, cls } = TX_STATUS[status] || { label: status, cls: 'bg-gray-700 text-gray-300' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
};

// ─── Página principal ─────────────────────────────────────────────────────────
const Transactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [total, setTotal]               = useState(0);
    const [page, setPage]                 = useState(1);
    const [loading, setLoading]           = useState(true);
    const [refreshing, setRefreshing]     = useState(false);
    const [error, setError]               = useState('');

    // Filtros
    const [filterType,   setFilterType]   = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [searchQuery,  setSearchQuery]  = useState('');
    const [searchInput,  setSearchInput]  = useState('');

    // Resumen
    const [summary, setSummary] = useState(null);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const fetchTransactions = useCallback(async (opts = {}) => {
        const currentPage   = opts.page   ?? page;
        const currentType   = opts.type   ?? filterType;
        const currentStatus = opts.status ?? filterStatus;
        const currentSearch = opts.search ?? searchQuery;

        if (!opts.silent) setLoading(true);
        else setRefreshing(true);

        try {
            const params = new URLSearchParams({
                page:  currentPage,
                limit: PAGE_SIZE,
                ...(currentType   && { type:   currentType }),
                ...(currentStatus && { status: currentStatus }),
                ...(currentSearch && { search: currentSearch }),
            });
            const res = await axios.get(`/admin/transactions?${params}`);
            if (res.data.success) {
                setTransactions(res.data.data.transactions);
                setTotal(res.data.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error al cargar transacciones');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [page, filterType, filterStatus, searchQuery]);

    // Resumen global (sin filtros)
    const fetchSummary = useCallback(async () => {
        try {
            const [todayRes, pendingRes] = await Promise.all([
                axios.get('/admin/transactions?status=COMPLETED&type=DEPOSIT&limit=1000&page=1'),
                axios.get('/admin/transactions?status=PENDING&limit=1&page=1'),
            ]);
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const todayTx = (todayRes.data.data?.transactions || []).filter(
                t => new Date(t.createdAt) >= today
            );
            setSummary({
                todayCount:   todayTx.length,
                todayAmount:  todayTx.reduce((s, t) => s + t.amount, 0),
                pendingCount: pendingRes.data.data?.total ?? 0,
                totalDeposits:(todayRes.data.data?.transactions || []).reduce((s, t) => s + t.amount, 0),
            });
        } catch {}
    }, []);

    useEffect(() => { fetchTransactions(); fetchSummary(); }, []);

    // Resetear a página 1 cuando cambian filtros
    useEffect(() => {
        setPage(1);
        fetchTransactions({ page: 1 });
    }, [filterType, filterStatus, searchQuery]);

    useEffect(() => { fetchTransactions(); }, [page]);

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchQuery(searchInput);
    };

    const handleRefresh = () => {
        fetchTransactions({ silent: true });
        fetchSummary();
    };

    return (
        <div className="space-y-6">

            {/* ── Encabezado ──────────────────────────────────────────────── */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Transacciones</h1>
                    <p className="text-sm text-gray-500 mt-0.5">{total} registros encontrados</p>
                </div>
                <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
                    style={{ backgroundColor: '#122436' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1a3a5c'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#122436'}
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'Actualizando...' : 'Actualizar'}
                </button>
            </div>

            {/* ── Tarjetas resumen ────────────────────────────────────────── */}
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                <SummaryCard
                    icon={TrendingUp} iconColor="#22c55e"
                    label="Depósitos hoy"
                    value={summary?.todayCount ?? '—'}
                    sub={summary ? `${summary.todayAmount} tickets` : undefined}
                />
                <SummaryCard
                    icon={CreditCard} iconColor="#2284DF"
                    label="Total depósitos"
                    value={summary ? `${summary.totalDeposits}` : '—'}
                    sub="tickets completados (histórico)"
                />
                <SummaryCard
                    icon={Clock} iconColor="#f59e0b"
                    label="Pendientes"
                    value={summary?.pendingCount ?? '—'}
                    sub="en espera de confirmación"
                />
                <SummaryCard
                    icon={TrendingDown} iconColor="#9ca3af"
                    label="Total registros"
                    value={total}
                    sub="con filtros actuales"
                />
            </div>

            {/* ── Filtros ─────────────────────────────────────────────────── */}
            <div
                className="rounded-xl p-4 flex flex-wrap gap-3 items-center"
                style={{ backgroundColor: '#0E2238' }}
            >
                {/* Tipo */}
                <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    className="rounded-lg px-3 py-2 text-sm text-gray-300 border-0 focus:ring-1 focus:ring-blue-500"
                    style={{ backgroundColor: '#122436' }}
                >
                    <option value="">Todos los tipos</option>
                    {Object.entries(TX_TYPES).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>

                {/* Estado */}
                <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    className="rounded-lg px-3 py-2 text-sm text-gray-300 border-0 focus:ring-1 focus:ring-blue-500"
                    style={{ backgroundColor: '#122436' }}
                >
                    <option value="">Todos los estados</option>
                    {Object.entries(TX_STATUS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </select>

                {/* Búsqueda */}
                <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                            type="text"
                            placeholder="Buscar por usuario o email..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm text-gray-300 placeholder-gray-600 border-0 focus:ring-1 focus:ring-blue-500"
                            style={{ backgroundColor: '#122436' }}
                        />
                    </div>
                    <button
                        type="submit"
                        className="px-3 py-2 rounded-lg text-sm text-white font-semibold"
                        style={{ backgroundColor: '#2284DF' }}
                    >
                        Buscar
                    </button>
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => { setSearchQuery(''); setSearchInput(''); }}
                            className="p-2 rounded-lg text-gray-400 hover:text-white"
                        >
                            <XCircle className="w-4 h-4" />
                        </button>
                    )}
                </form>
            </div>

            {/* ── Error ───────────────────────────────────────────────────── */}
            {error && (
                <div className="p-4 rounded-lg text-red-400 text-sm" style={{ backgroundColor: '#2d1a1a' }}>
                    {error}
                </div>
            )}

            {/* ── Tabla ───────────────────────────────────────────────────── */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#0E2238' }}>
                {loading ? (
                    <div className="flex items-center justify-center h-48">
                        <RefreshCw className="w-8 h-8 animate-spin text-gray-500" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead style={{ backgroundColor: '#0A1929' }}>
                                <tr>
                                    {['Usuario', 'Tipo', 'Monto', 'Estado', 'Descripción', 'Torneo', 'Fecha'].map(h => (
                                        <th key={h}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((tx) => (
                                    <tr key={tx.id} className="border-t hover:bg-white/5 transition-colors"
                                        style={{ borderColor: '#122436' }}>
                                        {/* Usuario */}
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                                                     style={{ backgroundColor: '#2284DF' }}>
                                                    {(tx.user?.username?.[0] || '?').toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-white leading-tight">
                                                        {tx.user?.username ?? 'N/A'}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{tx.user?.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        {/* Tipo */}
                                        <td className="px-4 py-3"><TypeBadge type={tx.type} /></td>
                                        {/* Monto */}
                                        <td className="px-4 py-3">
                                            <span className={`font-semibold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                {tx.amount > 0 ? '+' : ''}{tx.amount}
                                            </span>
                                            <span className="text-gray-500 text-xs ml-1">T</span>
                                        </td>
                                        {/* Estado */}
                                        <td className="px-4 py-3"><StatusBadge status={tx.status} /></td>
                                        {/* Descripción */}
                                        <td className="px-4 py-3 text-gray-400 max-w-[180px] truncate">
                                            {tx.description || '—'}
                                        </td>
                                        {/* Torneo */}
                                        <td className="px-4 py-3 text-gray-400 text-xs">
                                            {tx.tournament?.name || '—'}
                                        </td>
                                        {/* Fecha */}
                                        <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs">
                                            {fmt(tx.createdAt)}
                                        </td>
                                    </tr>
                                ))}
                                {transactions.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                                            Sin transacciones para los filtros seleccionados
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Paginación ──────────────────────────────────────────────── */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                        Mostrando {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="p-2 rounded-lg text-gray-400 disabled:opacity-30 transition-colors"
                            style={{ backgroundColor: '#0E2238' }}
                            onMouseEnter={e => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#122436')}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#0E2238'}
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>

                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                            return (
                                <button
                                    key={p}
                                    onClick={() => setPage(p)}
                                    className="w-8 h-8 rounded-lg text-sm font-semibold transition-colors"
                                    style={{
                                        backgroundColor: p === page ? '#2284DF' : '#0E2238',
                                        color: p === page ? 'white' : '#9ca3af',
                                    }}
                                >
                                    {p}
                                </button>
                            );
                        })}

                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="p-2 rounded-lg text-gray-400 disabled:opacity-30 transition-colors"
                            style={{ backgroundColor: '#0E2238' }}
                            onMouseEnter={e => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#122436')}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#0E2238'}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Transactions;
